import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StatusContext = createContext({ statuses: [], metaMap: {}, options: [], reload: () => {}, stages: [], stageMap: {}, reloadStages: () => {} });

const COLOR_CLS = {
  amber: "bg-[#FBEEE6] text-[#B45309] border-[#F0D8C4]",
  green: "bg-[#E8ECE8] text-[#0B3D3D] border-[#CFDDD0]",
  emerald: "bg-[#E3F0E5] text-[#1E7A34] border-[#C4E0C9]",
  red: "bg-[#F7E4DF] text-[#C24127] border-[#EBC7BE]",
  gray: "bg-[#ECECEC] text-[#5C635C] border-[#DADADA]",
  blue: "bg-[#E4ECF7] text-[#1D4E89] border-[#C6D6EC]",
  purple: "bg-[#EEE7F7] text-[#6B3FA0] border-[#D8C9EC]",
};

export const STATUS_COLORS = Object.keys(COLOR_CLS);
export function colorClass(color) { return COLOR_CLS[color] || COLOR_CLS.gray; }

export function StatusProvider({ children }) {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);
  const [stages, setStages] = useState([]);
  const reload = useCallback(
    () => api.get("/statuses").then((r) => setStatuses(r.data)).catch(() => {}),
    []
  );
  const reloadStages = useCallback(
    () => api.get("/stages").then((r) => setStages(r.data)).catch(() => {}),
    []
  );
  useEffect(() => { if (user) { reload(); reloadStages(); } }, [user, reload, reloadStages]);

  const metaMap = useMemo(() => {
    const m = {};
    for (const s of statuses) m[s.key] = { ...s, cls: colorClass(s.color) };
    return m;
  }, [statuses]);
  const stageMap = useMemo(() => {
    const m = {};
    for (const s of stages) m[s.key] = { ...s, cls: colorClass(s.color) };
    return m;
  }, [stages]);
  const options = useMemo(() => statuses.map((s) => s.key), [statuses]);
  const value = useMemo(
    () => ({ statuses, metaMap, options, reload, stages, stageMap, reloadStages }),
    [statuses, metaMap, options, reload, stages, stageMap, reloadStages]
  );

  return (
    <StatusContext.Provider value={value}>
      {children}
    </StatusContext.Provider>
  );
}

export const useStatuses = () => useContext(StatusContext);
